/**
 * Date Range Picker UI Rendering
 * Grid builders and UI components for date selection
 */
(function(window){
  'use strict';

  window.LiteDRPUI = class {
    constructor(pickerInstance){
      this.picker = pickerInstance;
    }

    buildDecadeGrid(which){
      const c = this.picker._getConstraints(which);
      let minY = (c.min ? c.min.getFullYear() : (this.picker.minDate ? this.picker.minDate.getFullYear() : window.LiteDRPUtils.getGlobalMinYear()));
      let maxY = (c.max ? c.max.getFullYear() : (this.picker.maxDate ? this.picker.maxDate.getFullYear() : (new Date()).getFullYear()));
      const startDec = Math.floor(minY/10)*10;
      const endDec = Math.floor(maxY/10)*10;
      const items = [];
      // Helper function to get translations
      function translate(key) {
        if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
          const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
          const translation = window.TranslationRegistry.translate(key, lang);
          if (translation !== key) return translation;
        }
        if (window.LanguageManager && typeof window.LanguageManager.translate === 'function') {
          const translation = window.LanguageManager.translate(key);
          if (translation !== key) return translation;
        }
        return key === 'drp_decades' ? 'Decades' : key;
      }
      const header = translate('drp_decades');
      items.push(`<div class="drp-grid-header mb-2 text-center fw-semibold">${header}</div>`);
      items.push('<div class="drp-grid d-grid" style="grid-template-columns: repeat(4, auto); gap:3px;">');
      const s = this.picker.sel[which] || {};
      const currentDate = which === 'from' ? this.picker._currentFromDate() : this.picker._currentToDate();
      const currentDecade = currentDate ? Math.floor(currentDate.getFullYear() / 10) * 10 : null;
      const restored = this.picker.restoredDate[which];
      const restoredDecade = restored ? Math.floor(restored.getFullYear() / 10) * 10 : null;
      const allDecades = new Set();
      for (let d=startDec; d<=endDec; d+=10) allDecades.add(d);
      if (currentDecade && (currentDecade < startDec || currentDecade > endDec)) {
        allDecades.add(currentDecade);
        if (currentDecade < startDec) allDecades.add(currentDecade - 10);
        if (currentDecade > endDec) allDecades.add(currentDecade + 10);
      }
      if (restoredDecade && (restoredDecade < startDec || restoredDecade > endDec)) {
        allDecades.add(restoredDecade);
        if (restoredDecade < startDec) allDecades.add(restoredDecade - 10);
        if (restoredDecade > endDec) allDecades.add(restoredDecade + 10);
      }
      if (s.decade && (s.decade < startDec || s.decade > endDec)) {
        allDecades.add(s.decade);
        if (s.decade < startDec) allDecades.add(s.decade - 10);
        if (s.decade > endDec) allDecades.add(s.decade + 10);
      }
      const sortedDecades = Array.from(allDecades).sort((a,b) => a-b);
      for (const d of sortedDecades) {
        const isActive = (s.decade === d || restoredDecade === d || currentDecade === d) ? ' active btn-primary' : ' btn-outline-secondary';
        const decadeText = `${d}<span class="drp-decade-s">s</span>`;
        items.push(`<button type="button" class="btn btn-sm drp-decade-item${isActive}" data-y="${d}">${decadeText}</button>`);
      }
      items.push('</div>');
      return items.join('');
    }

    buildYearGrid(which, decadeStart){
      // Validate decadeStart - use default if invalid
      if (!decadeStart || isNaN(decadeStart)) {
        const currentDate = which === 'from' ? this.picker._currentFromDate() : this.picker._currentToDate();
        const currentYear = currentDate ? currentDate.getFullYear() : (this.picker.minDate ? this.picker.minDate.getFullYear() : window.LiteDRPUtils.getGlobalMinYear());
        decadeStart = window.LiteDRPUtils.roundDownDecade(currentYear);
      }
      // For "from": use global bounds to show all years in decade
      // For "to": use constraints to respect cross-limits with "from"
      let minY, maxY;
      if (which === 'from') {
        minY = this.picker.minDate ? this.picker.minDate.getFullYear() : window.LiteDRPUtils.getGlobalMinYear();
        maxY = this.picker.maxDate ? this.picker.maxDate.getFullYear() : (new Date()).getFullYear();
      } else {
        const c = this.picker._getConstraints(which);
        minY = c.min ? c.min.getFullYear() : (this.picker.minDate ? this.picker.minDate.getFullYear() : window.LiteDRPUtils.getGlobalMinYear());
        maxY = c.max ? c.max.getFullYear() : (this.picker.maxDate ? this.picker.maxDate.getFullYear() : (new Date()).getFullYear());
      }
      const fromY = Math.max(decadeStart, minY);
      const toY = Math.min(decadeStart+9, maxY);
      const items = [];
      const s = this.picker.sel[which] || {};
      const current = this.picker.currentStage[which] || 'decade';
      const headerText = current === 'year' && s.year ? s.year : `${decadeStart}<span class="drp-decade-s">s</span>`;
      items.push(`<div class="d-flex justify-content-between align-items-center mb-2">
        <button type="button" class="btn btn-sm btn-light drp-nav-decade-left" aria-label="Previous decade">←</button>
        <div class="text-center fw-semibold flex-grow-1 drp-back-decade" title="Back to decades">${headerText}</div>
        <button type="button" class="btn btn-sm btn-light drp-nav-decade-right" aria-label="Next decade">→</button>
      </div>`);
      items.push('<div class="drp-grid d-grid" style="grid-template-columns: repeat(4, auto); gap:3px;">');
      const currentDate = which === 'from' ? this.picker._currentFromDate() : this.picker._currentToDate();
      const currentYear = currentDate ? currentDate.getFullYear() : null;
      const restored = this.picker.restoredDate[which];
      const restoredYear = restored ? restored.getFullYear() : null;
      for (let y=fromY; y<=toY; y++) {
        const isActive = (s.year === y || restoredYear === y || currentYear === y) ? ' active btn-primary' : ' btn-outline-secondary';
        items.push(`<button type="button" class="btn btn-sm drp-year-item${isActive}" data-y="${y}">${y}</button>`);
      }
      items.push('</div>');
      return items.join('');
    }

    buildMonthGrid(which, year){
      // Validate year - use default if invalid
      if (!year || isNaN(year)) {
        const currentDate = which === 'from' ? this.picker._currentFromDate() : this.picker._currentToDate();
        year = currentDate ? currentDate.getFullYear() : (this.picker.minDate ? this.picker.minDate.getFullYear() : window.LiteDRPUtils.getGlobalMinYear());
      }
      const names=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const items=[];
      items.push(`<div class="d-flex justify-content-between align-items-center mb-2">
        <button type="button" class="btn btn-sm btn-light drp-nav-year-left" aria-label="Previous year">←</button>
        <div class="text-center fw-semibold flex-grow-1 drp-back-year" title="Back to years">${year}</div>
        <button type="button" class="btn btn-sm btn-light drp-nav-year-right" aria-label="Next year">→</button>
      </div>`);
      const c = this.picker._getConstraints(which);
      let startM = 1, endM = 12;
      if (which === 'to' && c.min && c.min.getFullYear() === year) startM = Math.max(startM, c.min.getMonth()+1);
      if (which === 'from' && c.max && c.max.getFullYear() === year) endM = Math.min(endM, c.max.getMonth()+1);
      items.push('<div class="drp-grid d-grid" style="grid-template-columns: repeat(4, auto); gap:3px;">');
      const s = this.picker.sel[which] || {};
      const currentDate = which === 'from' ? this.picker._currentFromDate() : this.picker._currentToDate();
      const currentMonth = currentDate && currentDate.getFullYear() === year ? currentDate.getMonth() + 1 : null;
      const restored = this.picker.restoredDate[which];
      const restoredMonth = restored && restored.getFullYear() === year ? restored.getMonth() + 1 : null;
      for (let m=startM;m<=endM;m++) {
        const isActive = (s.month === m || restoredMonth === m || currentMonth === m) ? ' active btn-primary' : ' btn-outline-secondary';
        items.push(`<button type="button" class="btn btn-sm drp-month-item${isActive}" data-m="${m}">${names[m-1]}</button>`);
      }
      items.push('</div>');
      return items.join('');
    }

    buildDayGrid(which, year, month){
      // Validate year and month - use defaults if invalid
      if (!year || isNaN(year)) {
        const currentDate = which === 'from' ? this.picker._currentFromDate() : this.picker._currentToDate();
        year = currentDate ? currentDate.getFullYear() : (this.picker.minDate ? this.picker.minDate.getFullYear() : window.LiteDRPUtils.getGlobalMinYear());
      }
      if (!month || isNaN(month) || month < 1 || month > 12) {
        const currentDate = which === 'from' ? this.picker._currentFromDate() : this.picker._currentToDate();
        month = currentDate ? currentDate.getMonth() + 1 : 1;
      }
      const daysInMonth = new Date(year, month, 0).getDate();
      const items=[];
      const monthNames=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const monthLabel = monthNames[month-1] || String(month).padStart(2,'0');
      const activeDay = (this.picker.sel && this.picker.sel[which] && this.picker.sel[which].year === year && this.picker.sel[which].month === month) ? this.picker.sel[which].day : null;
      const currentDate = which === 'from' ? this.picker._currentFromDate() : this.picker._currentToDate();
      const currentDay = currentDate && currentDate.getFullYear() === year && currentDate.getMonth() + 1 === month ? currentDate.getDate() : null;
      const restored = this.picker.restoredDate[which];
      const restoredDay = restored && restored.getFullYear() === year && restored.getMonth() + 1 === month ? restored.getDate() : null;
      items.push(`<div class="d-flex justify-content-between align-items-center mb-2">
        <button type="button" class="btn btn-sm btn-light drp-nav-month-left" aria-label="Previous month">←</button>
        <div class="text-center fw-semibold flex-grow-1 drp-back-month" title="Back to months">${year}-${monthLabel}</div>
        <button type="button" class="btn btn-sm btn-light drp-nav-month-right" aria-label="Next month">→</button>
      </div>`);
      const c = this.picker._getConstraints(which);
      let startD = 1, endD = daysInMonth;
      if (which === 'to' && c.min && c.min.getFullYear() === year && (c.min.getMonth()+1) === month) startD = Math.max(startD, c.min.getDate());
      if (which === 'from' && c.max && c.max.getFullYear() === year && (c.max.getMonth()+1) === month) endD = Math.min(endD, c.max.getDate());
      items.push('<div class="drp-grid d-grid" style="grid-template-columns: repeat(7, auto); gap:3px;">');
      for (let d=startD; d<=endD; d++) {
        const isActive = (activeDay && d === activeDay) || (restoredDay && d === restoredDay) || (currentDay && d === currentDay) ? ' active' : '';
        items.push(`<button type="button" class="btn btn-sm btn-outline-secondary drp-day-item${isActive}" data-d="${d}">${d}</button>`);
      }
      items.push('</div>');
      return items.join('');
    }

    buildExactPaneHtml(fromText, toText, placeholder, fromLabel, toLabel){
      const column = (which, labelText, label) => {
        const inputId = which === 'from' ? 'drp-input-from' : 'drp-input-to';
        const inputClass = which === 'from' ? 'drp-input-from' : 'drp-input-to';
        return `
        <div class="drp-column" data-which="${which}">
          <div class="drp-inputs mb-2">
            <div class="input-group input-group-sm">
              <span class="input-group-text">${labelText}</span>
              <input type="text" class="form-control ${inputClass}" id="${inputId}" placeholder="${placeholder}" aria-label="${label}" />
            </div>
          </div>
          <div class="drp-panel" data-which="${which}">
            <div class="drp-stage drp-stage-decade"></div>
            <div class="drp-stage drp-stage-year d-none"></div>
            <div class="drp-stage drp-stage-month d-none"></div>
            <div class="drp-stage drp-stage-day d-none"></div>
          </div>
        </div>`;
      };
      return `<div class="d-flex drp-exact-columns" style="align-items:flex-start;">${column('from', fromText, fromLabel)}${column('to', toText, toLabel)}</div>`;
    }

    buildYearOptions(){
      let minY = this.picker.minDate ? this.picker.minDate.getFullYear() : window.LiteDRPUtils.getGlobalMinYear();
      let maxY = this.picker.maxDate ? this.picker.maxDate.getFullYear() : (new Date()).getFullYear();
      if (minY > maxY) { const t=minY; minY=maxY; maxY=t; }
      const parts = ['<option value="">Year…</option>'];
      for (let y=minY; y<=maxY; y++) parts.push(`<option value="${y}">${y}</option>`);
      return parts.join('');
    }

    buildDecadeOptions(){
      let minY = this.picker.minDate ? this.picker.minDate.getFullYear() : window.LiteDRPUtils.getGlobalMinYear();
      let maxY = this.picker.maxDate ? this.picker.maxDate.getFullYear() : (new Date()).getFullYear();
      const start = Math.floor(minY/10)*10; const end = Math.floor(maxY/10)*10;
      const parts = ['<option value="">Decade…</option>'];
      for (let d=start; d<=end; d+=10) parts.push(`<option value="${d}">${d}s–${d+9}</option>`);
      return parts.join('');
    }
  };
})(window);

